import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { FolderKanban, GitBranch, Lock, Pencil, Plus, Settings, X } from 'lucide-react';
import type { CompletionPolicy } from '@vwt/contracts';
import { Badge, Button, Checkbox, Dialog, DialogContent, DialogFooter, EmptyState, Field, Input, NativeSelect, Textarea, toast } from '@vwt/ui';
import { newIdempotencyKey, POLL_ACTIVE, useApiMutation, useApiQuery } from '../../lib/api.js';
import { fieldErrors, toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { useMe } from '../../lib/session.js';
import { KeyLabel, VisibilityBadge } from '../../components/badges.js';
import { PageHeader, PrincipalPicker, QueryState, SectionTitle, UserChip } from '../../components/common.js';
import { MarkdownView } from '../../components/markdown.js';
import { VisibilityDialog } from '../../components/visibility.js';
import { RepositoryDialog } from '../../components/repository-dialog.js';
import { ScopedWork, useScopedView } from '../work/scoped-work.js';
import { ProjectStatusBadge, ProjectUpdates } from './project-updates.js';

export function ProjectsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const projects = useApiQuery('listProjects', { query: {} }, { refetchInterval: POLL_ACTIVE });
  const [open, setOpen] = useState(false);
  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('projects.title')}
        subtitle={t('projects.subtitle')}
        actions={
          me.permissions.canCreateProject ? (
            <Button variant="primary" onClick={() => setOpen(true)}>
              <Plus /> {t('projects.new')}
            </Button>
          ) : null
        }
      />
      <div className="p-4 md:p-6">
        <QueryState query={projects} empty={projects.data?.data.length === 0 ? <EmptyState icon={<FolderKanban />} title={t('projects.empty')} /> : undefined}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {(projects.data?.data ?? []).map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="rounded-[var(--radius-lg)] border border-border bg-surface p-4 shadow-xs hover:border-border-strong">
                <div className="flex items-center gap-2">
                  <span className="flex size-8 items-center justify-center rounded-[var(--radius)] bg-accent-soft text-xs font-bold text-accent-soft-fg">{p.key.slice(0, 3)}</span>
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{p.name}</p>
                    <p className="text-xs text-fg-subtle">{p.key}</p>
                  </div>
                  {p.visibility === 'RESTRICTED' ? <Lock className="ml-auto size-4 text-warning" aria-label={t('status.visibility.RESTRICTED')} /> : null}
                </div>
                <p className="mt-3 line-clamp-2 min-h-10 text-sm text-fg-muted">{p.descriptionMarkdown.replace(/[*_`#>]/g, '') || '—'}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
                  {p.latestUpdate ? <ProjectStatusBadge status={p.latestUpdate.status} /> : null}
                  <Badge tone="info">{t('projects.openWork', { count: p.openWorkCount })}</Badge>
                  {p.repositories.length ? (
                    <span className="flex items-center gap-1">
                      <GitBranch className="size-3" /> {p.repositories.length}
                    </span>
                  ) : null}
                  {p.owningTeams.map((team) => (
                    <span key={team.id}>· {team.name}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </QueryState>
      </div>
      <CreateProjectDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function CreateProjectDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const teams = useApiQuery('listTeams', { query: {} }, { enabled: open });
  const create = useApiMutation('createProject', { invalidate: ['listProjects'] });
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [teamId, setTeamId] = useState('');
  const [policy, setPolicy] = useState<CompletionPolicy>('PRODUCTION');
  const [requiresReview, setRequiresReview] = useState(false);
  const [visibility, setVisibility] = useState<'COMPANY' | 'RESTRICTED'>('COMPANY');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const submit = async () => {
    try {
      const result = await create.mutateAsync({ idempotencyKey: newIdempotencyKey(), body: { key, name, descriptionMarkdown: description, visibility, defaultCompletionPolicy: policy, requiresReview, ...(teamId ? { owningTeamIds: [teamId] } : {}) } });
      onOpenChange(false);
      navigate(`/projects/${result!.data.id}`);
    } catch (error) {
      setErrors(fieldErrors(error));
      toastError(error);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('projects.new')}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="grid grid-cols-[120px_1fr] gap-3">
            <Field label={t('projects.key')} htmlFor="p-key" required error={errors.key}>
              <Input id="p-key" value={key} onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))} placeholder="RIDER" />
            </Field>
            <Field label={t('projects.name')} htmlFor="p-name" required error={errors.name}>
              <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          <Field label={t('projects.description')} htmlFor="p-desc">
            <Textarea id="p-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label={t('projects.owningTeams')} htmlFor="p-team">
              <NativeSelect id="p-team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                <option value="">—</option>
                {(teams.data?.data ?? []).map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('projects.defaultPolicy')} htmlFor="p-policy">
              <NativeSelect id="p-policy" value={policy} onChange={(e) => setPolicy(e.target.value as CompletionPolicy)}>
                {(['PRODUCTION', 'DELIVERABLE', 'REVIEWED_DELIVERABLE'] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`status.policy.${p}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('projects.visibility')} htmlFor="p-vis">
              <NativeSelect id="p-vis" value={visibility} onChange={(e) => setVisibility(e.target.value as 'COMPANY')}>
                <option value="COMPANY">{t('status.visibility.COMPANY')}</option>
                <option value="RESTRICTED">{t('status.visibility.RESTRICTED')}</option>
              </NativeSelect>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={requiresReview} onCheckedChange={(v) => setRequiresReview(v === true)} /> {t('projects.requiresReview')}
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={create.isPending} disabled={!key || !name.trim()}>
              {t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const project = useApiQuery('getProject', { params: { id: id! } }, { refetchInterval: POLL_ACTIVE });
  const view = useScopedView();
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reposOpen, setReposOpen] = useState(false);
  const setRepositories = useApiMutation('setProjectRepositories', { invalidate: ['getProject', 'listProjects', 'listRepositories'] });
  const p = project.data?.data;
  return (
    <QueryState query={project}>
      {p ? (
        <div className="flex h-full flex-col">
          <PageHeader
            title={
              <span className="flex items-center gap-2">
                {p.name} <KeyLabel value={p.key} /> <VisibilityBadge visibility={p.visibility} />
                {p.latestUpdate ? <ProjectStatusBadge status={p.latestUpdate.status} /> : null}
              </span>
            }
            subtitle={t(`status.policy.${p.defaultCompletionPolicy}`)}
            actions={
              p.availableActions.includes('edit') ? (
                <>
                  <Button onClick={() => setVisibilityOpen(true)}>
                    <Lock /> {t('projects.visibility')}
                  </Button>
                  <Button onClick={() => setSettingsOpen(true)}>
                    <Settings /> {t('projects.settings')}
                  </Button>
                </>
              ) : null
            }
          />
          <div className={`mx-auto grid w-full grid-cols-1 gap-6 p-4 md:p-6 ${view === 'board' ? 'max-w-none' : 'max-w-6xl lg:grid-cols-[minmax(0,1fr)_280px]'}`}>
            <div className="flex min-w-0 flex-col gap-6">
              <ScopedWork scope={{ projectId: p.id }} />
              <ProjectUpdates project={p} />
            </div>
            <aside className="flex flex-col gap-5">
              {p.descriptionMarkdown ? <MarkdownView source={p.descriptionMarkdown} /> : null}
              <div>
                <SectionTitle>{t('projects.managers')}</SectionTitle>
                <div className="flex flex-col gap-1">
                  {p.managers.map((m) => (
                    <UserChip key={m.id} user={m} />
                  ))}
                </div>
              </div>
              <div>
                <SectionTitle>{t('projects.owningTeams')}</SectionTitle>
                {p.owningTeams.map((team) => (
                  <Link key={team.id} to={`/teams/${team.id}`} className="block text-sm hover:underline">
                    {team.name}
                  </Link>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <SectionTitle>{t('projects.repositories')}</SectionTitle>
                  {p.availableActions.includes('edit') ? (
                    <Button variant="ghost" size="iconSm" aria-label={t('repoPicker.edit')} onClick={() => setReposOpen(true)}>
                      <Pencil />
                    </Button>
                  ) : null}
                </div>
                {p.repositories.length ? (
                  p.repositories.map((r) => (
                    <p key={r.id} className="flex items-center gap-1 text-xs">
                      <GitBranch className="size-3" /> {r.fullName}
                    </p>
                  ))
                ) : (
                  <p className="text-xs text-fg-subtle">{t('work.detail.noRepositories')}</p>
                )}
              </div>
            </aside>
          </div>
          <VisibilityDialog open={visibilityOpen} onOpenChange={setVisibilityOpen} resource={{ kind: 'project', id: p.id }} />
          {settingsOpen ? <ProjectSettingsDialog project={p} onClose={() => setSettingsOpen(false)} /> : null}
          {reposOpen ? (
            <RepositoryDialog
              title={t('repoPicker.projectTitle')}
              description={t('repoPicker.projectHint')}
              initial={p.repositories}
              onSave={async (repositoryIds) => {
                try {
                  await setRepositories.mutateAsync({ params: { id: p.id }, body: { expectedVersion: p.version, repositoryIds } });
                  toast.success(t('common.changesSaved'));
                } catch (error) {
                  toastError(error);
                  throw error;
                }
              }}
              onClose={() => setReposOpen(false)}
            />
          ) : null}
        </div>
      ) : null}
    </QueryState>
  );
}

function ChipList({ items, onRemove }: { items: Array<{ id: string; name: string }>; onRemove: (id: string) => void }) {
  const { t } = useTranslation();
  if (!items.length) return <span className="text-xs text-fg-subtle">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <Badge key={item.id} tone="neutral" className="gap-1">
          {item.name}
          <button type="button" className="rounded hover:bg-muted" aria-label={t('common.removeItem', { name: item.name })} onClick={() => onRemove(item.id)}>
            <X className="size-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
}

function ProjectSettingsDialog({ project, onClose }: { project: NonNullable<ReturnType<typeof useApiQuery<'getProject'>>['data']>['data']; onClose: () => void }) {
  const { t } = useTranslation();
  const update = useApiMutation('updateProject', { invalidate: ['getProject', 'listProjects'] });
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.descriptionMarkdown);
  const [policy, setPolicy] = useState(project.defaultCompletionPolicy);
  const [requiresReview, setRequiresReview] = useState(project.requiresReview);
  const [managers, setManagers] = useState(project.managers.map((m) => ({ id: m.id, name: m.displayName })));
  const [teams, setTeams] = useState(project.owningTeams.map((tm) => ({ id: tm.id, name: tm.name })));
  const [archived, setArchived] = useState(!!project.archivedAt);
  const [staleDays, setStaleDays] = useState(project.automation.staleAfterDays?.toString() ?? '');
  const [staleAction, setStaleAction] = useState<'NOTIFY' | 'CANCEL'>(project.automation.staleAction ?? 'NOTIFY');
  const [archiveDays, setArchiveDays] = useState(project.automation.autoArchiveAfterDays?.toString() ?? '');
  const addUnique = (list: Array<{ id: string; name: string }>, item: { id: string; name: string } | null) => (item && !list.some((x) => x.id === item.id) ? [...list, item] : list);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('projects.settings')}>
        <form
          className="flex flex-col gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await update.mutateAsync({ params: { id: project.id }, body: { expectedVersion: project.version, name, descriptionMarkdown: description, defaultCompletionPolicy: policy, requiresReview, managerIds: managers.map((m) => m.id), owningTeamIds: teams.map((tm) => tm.id), archived, staleAfterDays: staleDays ? Number(staleDays) : null, staleAction, autoArchiveAfterDays: archiveDays ? Number(archiveDays) : null } });
              toast.success(t('common.changesSaved'));
              onClose();
            } catch (error) {
              toastError(error);
            }
          }}
        >
          <Field label={t('projects.name')} htmlFor="ps-name">
            <Input id="ps-name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('projects.description')} htmlFor="ps-desc">
            <Textarea id="ps-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
          </Field>
          <Field label={t('projects.defaultPolicy')} htmlFor="ps-policy">
            <NativeSelect id="ps-policy" value={policy} onChange={(e) => setPolicy(e.target.value as CompletionPolicy)}>
              {(['PRODUCTION', 'DELIVERABLE', 'REVIEWED_DELIVERABLE'] as const).map((p) => (
                <option key={p} value={p}>
                  {t(`status.policy.${p}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={requiresReview} onCheckedChange={(v) => setRequiresReview(v === true)} /> {t('projects.requiresReview')}
          </label>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{t('projects.managers')}</span>
            <ChipList items={managers} onRemove={(id) => setManagers((list) => list.filter((m) => m.id !== id))} />
            <PrincipalPicker label={t('projects.addManager')} placeholder={t('projects.addManager')} kinds="USER" value={null} allowClear={false} onChange={(picked) => setManagers((list) => addUnique(list, picked ? { id: picked.id, name: picked.name } : null))} />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{t('projects.owningTeams')}</span>
            <ChipList items={teams} onRemove={(id) => setTeams((list) => list.filter((tm) => tm.id !== id))} />
            <PrincipalPicker label={t('projects.addTeam')} placeholder={t('projects.addTeam')} kinds="TEAM" value={null} allowClear={false} onChange={(picked) => setTeams((list) => addUnique(list, picked ? { id: picked.id, name: picked.name } : null))} />
          </div>
          <fieldset className="flex flex-col gap-2 rounded-[var(--radius)] border border-border p-3">
            <legend className="px-1 text-sm font-medium">{t('projects.automation.title')}</legend>
            <p className="text-xs text-fg-subtle">{t('projects.automation.hint')}</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[8rem_1fr]">
              <Field label={t('projects.automation.staleAfter')} htmlFor="ps-stale">
                <Input id="ps-stale" type="number" min={3} max={365} value={staleDays} onChange={(e) => setStaleDays(e.target.value)} placeholder={t('projects.automation.off')} />
              </Field>
              <Field label={t('projects.automation.staleAction')} htmlFor="ps-stale-action">
                <NativeSelect id="ps-stale-action" value={staleAction} disabled={!staleDays} onChange={(e) => setStaleAction(e.target.value as 'NOTIFY' | 'CANCEL')}>
                  <option value="NOTIFY">{t('projects.automation.actions.NOTIFY')}</option>
                  <option value="CANCEL">{t('projects.automation.actions.CANCEL')}</option>
                </NativeSelect>
              </Field>
            </div>
            <Field label={t('projects.automation.autoArchive')} htmlFor="ps-archive" hint={t('projects.automation.autoArchiveHint')}>
              <Input id="ps-archive" type="number" min={1} max={730} value={archiveDays} onChange={(e) => setArchiveDays(e.target.value)} placeholder={t('projects.automation.off')} className="w-32" />
            </Field>
          </fieldset>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={archived} onCheckedChange={(v) => setArchived(v === true)} /> {t('projects.archive')}
          </label>
          {archived && !project.archivedAt ? <p className="text-xs text-warning">{t('projects.archiveHint')}</p> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={update.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
